import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { User } from '@supabase/supabase-js'
import type { AppSettings, CalendarEvent, CalendarView, EventFormData, SyncStatus, ThemeMode } from '../types'
import { createId, loadEvents, loadSettings, saveEvents, saveSettings } from '../utils/storage'
import { getSupabase, isCloudConfigured } from '../lib/supabase'
import { pullAndPush, pushEvent, pushSettings } from '../lib/sync'
import type { TabId } from '../config'

interface SyncContextValue {
  events: CalendarEvent[]
  settings: AppSettings
  user: User | null
  status: SyncStatus
  statusLabel: string
  error: string | null
  configured: boolean
  addEvent: (data: EventFormData) => void
  updateEvent: (id: string, data: EventFormData) => void
  deleteEvent: (id: string) => void
  setActiveTab: (id: TabId) => void
  setCalendarView: (view: CalendarView) => void
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  syncNow: () => Promise<void>
}

const SyncContext = createContext<SyncContextValue | null>(null)

function stamp(partial: Omit<CalendarEvent, 'updatedAt'> & { updatedAt?: string }): CalendarEvent {
  return { ...partial, updatedAt: new Date().toISOString() }
}

const LABELS: Record<SyncStatus, string> = {
  local: 'Nur lokal',
  offline: 'Offline',
  syncing: 'Synchronisierung…',
  synced: 'Synchronisiert',
  error: 'Sync-Fehler',
  unconfigured: 'Cloud nicht eingerichtet',
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const configured = isCloudConfigured()
  const [events, setEvents] = useState<CalendarEvent[]>(() => loadEvents())
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings())
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<SyncStatus>(configured ? 'local' : 'unconfigured')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    saveEvents(events)
  }, [events])

  useEffect(() => {
    saveSettings(settings)
  }, [settings])

  useEffect(() => {
    document.documentElement.dataset.theme = settings.theme
    void window.neonDeck?.setNativeTheme?.(settings.theme)
  }, [settings.theme])

  const syncNow = useCallback(async () => {
    const sb = getSupabase()
    if (!sb || !navigator.onLine) {
      setStatus(!navigator.onLine ? 'offline' : configured ? 'local' : 'unconfigured')
      return
    }
    const {
      data: { session },
    } = await sb.auth.getSession()
    if (!session?.user) {
      setUser(null)
      setStatus('local')
      return
    }
    setUser(session.user)
    setStatus('syncing')
    try {
      const next = await pullAndPush(session.user.id, events, settings)
      setEvents(next.events)
      setSettings(next.settings)
      setError(null)
      setStatus('synced')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync fehlgeschlagen')
      setStatus('error')
    }
  }, [configured, events, settings])

  useEffect(() => {
    const sb = getSupabase()
    if (!sb) return
    sb.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null)
    })
    const { data } = sb.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (user) void syncNow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    const sb = getSupabase()
    if (!sb || !user) return
    const channel = sb
      .channel(`neon-deck-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'calendar_events', filter: `user_id=eq.${user.id}` },
        () => {
          void syncNow()
        },
      )
      .subscribe()
    return () => {
      void sb.removeChannel(channel)
    }
  }, [user, syncNow])

  const persistEvent = useCallback(
    (event: CalendarEvent) => {
      setEvents((prev) => {
        const idx = prev.findIndex((e) => e.id === event.id)
        if (idx === -1) return [...prev, event]
        const next = [...prev]
        next[idx] = event
        return next
      })
      if (user) {
        void pushEvent(user.id, event).catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Push fehlgeschlagen')
          setStatus('error')
        })
      }
    },
    [user],
  )

  const addEvent = useCallback(
    (data: EventFormData) => {
      persistEvent(
        stamp({
          id: createId(),
          title: data.title.trim(),
          start: data.start,
          end: data.end,
          notes: data.notes.trim() || undefined,
          color: data.color,
        }),
      )
    },
    [persistEvent],
  )

  const updateEvent = useCallback(
    (id: string, data: EventFormData) => {
      const current = events.find((e) => e.id === id)
      if (!current) return
      persistEvent(
        stamp({
          ...current,
          title: data.title.trim(),
          start: data.start,
          end: data.end,
          notes: data.notes.trim() || undefined,
          color: data.color,
        }),
      )
    },
    [events, persistEvent],
  )

  const deleteEvent = useCallback(
    (id: string) => {
      const current = events.find((e) => e.id === id)
      if (!current) return
      persistEvent(stamp({ ...current, deletedAt: new Date().toISOString() }))
    },
    [events, persistEvent],
  )

  const writeSettings = useCallback(
    (next: AppSettings) => {
      setSettings(next)
      if (user) {
        void pushSettings(user.id, next).catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'Einstellungen nicht gespeichert')
          setStatus('error')
        })
      }
    },
    [user],
  )

  const setActiveTab = useCallback(
    (id: TabId) => {
      writeSettings({ ...settings, activeTab: id, updatedAt: new Date().toISOString() })
    },
    [settings, writeSettings],
  )

  const setCalendarView = useCallback(
    (view: CalendarView) => {
      writeSettings({ ...settings, calendarView: view, updatedAt: new Date().toISOString() })
    },
    [settings, writeSettings],
  )

  const setTheme = useCallback(
    (theme: ThemeMode) => {
      writeSettings({ ...settings, theme, updatedAt: new Date().toISOString() })
    },
    [settings, writeSettings],
  )

  const toggleTheme = useCallback(() => {
    setTheme(settings.theme === 'light' ? 'dark' : 'light')
  }, [settings.theme, setTheme])

  const mapAuthError = (err: { message?: string; code?: string; status?: number }) => {
    const msg = (err.message || '').toLowerCase()
    const code = (err.code || '').toLowerCase()
    if (code === 'invalid_credentials' || msg.includes('invalid login credentials')) {
      return new Error('Falsches Passwort oder E-Mail. Tippe «Passwort vergessen» oder setze es neu.')
    }
    if (code === 'user_already_exists' || msg.includes('already registered') || msg.includes('user already')) {
      return new Error('Konto existiert schon — bitte anmelden statt neu erstellen.')
    }
    if (msg.includes('email not confirmed')) {
      return new Error('E-Mail noch nicht bestaetigt. Pruefe dein Postfach oder schalte die Bestaetigung in Supabase ab.')
    }
    return new Error(err.message || 'Anmeldung fehlgeschlagen')
  }

  const signIn = useCallback(async (email: string, password: string) => {
    const sb = getSupabase()
    if (!sb) throw new Error('Cloud ist noch nicht eingerichtet')
    const { error: authErr } = await sb.auth.signInWithPassword({ email, password })
    if (authErr) throw mapAuthError(authErr)
  }, [])

  const signUp = useCallback(async (email: string, password: string) => {
    const sb = getSupabase()
    if (!sb) throw new Error('Cloud ist noch nicht eingerichtet')
    const { data, error: authErr } = await sb.auth.signUp({ email, password })
    if (authErr) throw mapAuthError(authErr)
    if (data.user && !data.session) {
      throw new Error(
        'Konto erstellt. Bitte E-Mail bestaetigen, oder in Supabase Auth die E-Mail-Bestaetigung abschalten.',
      )
    }
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    const sb = getSupabase()
    if (!sb) throw new Error('Cloud ist noch nicht eingerichtet')
    const redirectTo =
      typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://luckys-place.pages.dev/'
    const { error: authErr } = await sb.auth.resetPasswordForEmail(email, { redirectTo })
    if (authErr) throw mapAuthError(authErr)
  }, [])

  const signOut = useCallback(async () => {
    const sb = getSupabase()
    if (!sb) return
    await sb.auth.signOut()
    setUser(null)
    setStatus(configured ? 'local' : 'unconfigured')
  }, [configured])

  const value = useMemo<SyncContextValue>(
    () => ({
      events,
      settings,
      user,
      status,
      statusLabel: LABELS[status],
      error,
      configured,
      addEvent,
      updateEvent,
      deleteEvent,
      setActiveTab,
      setCalendarView,
      setTheme,
      toggleTheme,
      signIn,
      signUp,
      signOut,
      resetPassword,
      syncNow,
    }),
    [
      events,
      settings,
      user,
      status,
      error,
      configured,
      addEvent,
      updateEvent,
      deleteEvent,
      setActiveTab,
      setCalendarView,
      setTheme,
      toggleTheme,
      signIn,
      signUp,
      signOut,
      resetPassword,
      syncNow,
    ],
  )

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

export function useSync() {
  const ctx = useContext(SyncContext)
  if (!ctx) throw new Error('useSync braucht SyncProvider')
  return ctx
}
